/**
 * Codec for the `emoji_unicode` wire format: one or more `U+XXXX` codepoints joined by single
 * spaces. The backend owns the format (`EMOJI_UNICODE_PATTERN` in types-and-constants) and
 * validates against it; this is the web half of the codec.
 *
 * Mobile keeps its own copy at `tach-mobile/app/utils/emoji.ts`, which this deliberately mirrors —
 * but the two are NOT in step today: tach-mobile#143 is still open against `release/v1.0.21`, so
 * the shipped mobile decoder is the old single-codepoint, case-sensitive one. Since the API
 * already accepts multi-codepoint values, mobile truncates every one of them right now (❤️ → ❤).
 * Keep the two in step once #143 lands.
 */

// Highest code point representable in UTF-16 — String.fromCodePoint throws a RangeError above it,
// so anything larger is dropped rather than allowed to crash a render.
const MAX_CODE_POINT = 0x10ffff;

// The UTF-16 surrogate range. These are not characters — they only mean anything as the halves of
// a surrogate PAIR, and the wire format never splits one (an astral codepoint is written whole,
// `U+1F477`). String.fromCodePoint accepts a lone surrogate without complaint and returns an
// unpaired code unit, which renders as �, so the range filter has to exclude them explicitly:
// this is the one band inside U+10FFFF that is well-formed, in range, and still unrenderable.
const SURROGATE_START = 0xd800;
const SURROGATE_END = 0xdfff;

const isRenderableCodePoint = (codePoint: number): boolean =>
  Number.isInteger(codePoint) &&
  codePoint >= 0 &&
  codePoint <= MAX_CODE_POINT &&
  !(codePoint >= SURROGATE_START && codePoint <= SURROGATE_END);

/**
 * Rebuilds an emoji from the space-joined `U+XXXX` form produced by emojiToUnicode.
 *
 * Multi-codepoint emojis must be rebuilt from ALL of their codepoints: ZWJ sequences
 * (👷‍♂️ = `U+1F477 U+200D U+2642 U+FE0F`), variation selectors (❤️ = `U+2764 U+FE0F`), keycaps
 * (1️⃣ = `U+31 U+FE0F U+20E3`) and skin-tone variants only render correctly when the whole sequence
 * goes to String.fromCodePoint at once. The previous form read a single codepoint —
 * `String.replace` with a string pattern strips only the first `U+`, and parseInt stops at the
 * first space — so every one of those collapsed to its base character (👷, ❤, `1`). THT-750.
 */
export const unicodeToEmoji = (unicode: string | undefined) => {
  if (!unicode) return '';

  const codePoints = unicode
    .trim()
    .split(/\s+/)
    // Remove the "U+" prefix and convert to hex number. Case-insensitive because the API accepts
    // either case, and `parseInt('u+1f4b0', 16)` is NaN, which throws on render.
    .map((part) => parseInt(part.replace(/^U\+/i, ''), 16))
    .filter(isRenderableCodePoint);

  if (codePoints.length === 0) return '';

  return String.fromCodePoint(...codePoints);
};

/**
 * Serializes an emoji to the wire format. Note this does NOT zero-pad — low codepoints come out
 * short (©️ → `U+A9 U+FE0F`), which is why the backend pattern allows 1-6 hex digits and not 4-6.
 */
export const emojiToUnicode = (emoji: string | undefined) => {
  if (!emoji) return '';

  return (
    Array.from(emoji)
      .map((char) => char.codePointAt(0))
      // Array.from splits by code point, so every element here has one and this drops nothing.
      // It is a guard, not a filter: the `codePointAt(0)?.toString(16)` this replaces would have
      // emitted the literal string "U+undefined" if it ever were empty, which is not hex, so the
      // save would come back as one more `"emojiUnicode" fails to match the required pattern`
      // 400 — the exact rejection the rest of this ticket is about.
      .filter((codePoint): codePoint is number => codePoint !== undefined)
      .map((codePoint) => `U+${codePoint.toString(16).toUpperCase()}`)
      .join(' ')
  );
};
